import React from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import {
  PlaceholderBar,
  PlaceholderBarItem,
  PlaceholderChip,
  PlaceholderDesktop,
  PlaceholderItem,
  PlaceholderMobile,
  PlaceholderStrip,
  PlaceholderTrigger,
} from './CategoryMegaNavPlaceholder.styles';
import { putHolidayFirst } from './holidaySeason';

const { saveTheDate, invitation, thankYou, holiday, enclosure, sign, menu, napkin } = StationeryTemplateCategoryEnum;

/**
 * The five slots, mirrored from `megaNav.config.ts` without importing it (that module is
 * part of the lazily loaded nav; this placeholder ships in the flag-off bundle).
 * `megaNav.config.spec.ts` asserts the two stay in sync.
 */
export const PLACEHOLDER_SLOTS = [
  { id: 'saveTheDate', category: saveTheDate, activeForCategories: [saveTheDate] },
  { id: 'invitation', category: invitation, activeForCategories: [invitation, enclosure] },
  { id: 'ceremonyReception', category: undefined, activeForCategories: [sign, menu, napkin] },
  { id: 'thankYou', category: thankYou, activeForCategories: [thankYou] },
  { id: 'holiday', category: holiday, activeForCategories: [holiday] },
] as const;

/**
 * Stand-in rendered while the `CategoryMegaNav` chunk loads: the same five slots in the
 * same geometry (desktop bar / mobile strip) as the real nav, but inert, so the swap to
 * the real nav causes no layout shift or scroll reset. Shares only `megaNav.geometry.ts`
 * with the real nav so the default (flag-off) bundle stays free of the nav's code.
 */
export const CategoryMegaNavPlaceholder = () => {
  const { tq } = useTranslation('stationery');
  const { currentCategory } = useCardCategoryContext();
  const getCategoryTitle = useStationeryTemplateCategoryTranslations('dashboardCategoryNav');
  const ceremonyReceptionLabel = tq`Ceremony & Reception`;
  const navLabel = tq`Category navigation`;
  const orderedSlots = putHolidayFirst(PLACEHOLDER_SLOTS, slot => slot.id === 'holiday');

  const slots = orderedSlots.map(slot => ({
    id: slot.id,
    label: slot.category ? getCategoryTitle(slot.category) : ceremonyReceptionLabel,
    isActive: (slot.activeForCategories as ReadonlyArray<StationeryTemplateCategoryEnum>).includes(currentCategory),
  }));

  return (
    <>
      <PlaceholderMobile
        aria-label={navLabel}
        aria-busy="true"
        data-testid="category-mega-nav-placeholder-mobile"
      >
        <PlaceholderStrip>
          {slots.map(slot => (
            <PlaceholderItem key={slot.id}>
              <PlaceholderChip $isActive={slot.isActive}>{slot.label}</PlaceholderChip>
            </PlaceholderItem>
          ))}
        </PlaceholderStrip>
      </PlaceholderMobile>
      <PlaceholderDesktop
        aria-label={navLabel}
        aria-busy="true"
        data-testid="category-mega-nav-placeholder"
      >
        <PlaceholderBar>
          {slots.map(slot => (
            <PlaceholderBarItem key={slot.id}>
              <PlaceholderTrigger $isActive={slot.isActive}>{slot.label}</PlaceholderTrigger>
            </PlaceholderBarItem>
          ))}
        </PlaceholderBar>
      </PlaceholderDesktop>
    </>
  );
};
